<template>
  <c-sidesheet id="type-modal" :active="displayModal" @close="close">
    <m-confirm ref="confirm" mode="danger" />

    <div class="modal-frame">
      <div class="modal-wrapper">
        <shadow-scroll-container ref="track" class="modal-track" :with-top-scroller="true">
          <div
            class="violation-type"
            :class="{
              'edit-mode': violation,
            }"
          >
            <div class="violation-type__wrapper">
              <div class="modal-frame__header">
                <h2 class="modal-frame__title">{{ modalTitle }}</h2>

                <div class="modal-frame__actions">
                  <c-button
                    v-if="violation && !violation.is_system"
                    size="small"
                    class="violation-type__remove"
                    label="Удалить"
                    :icon="{ icon: 'trash', color: 'secondary' }"
                    @click="remove"
                  />
                </div>
              </div>

              <div v-if="!violation || (violation && !violation.is_system)" class="form-group violation-type__type">
                <c-input
                  v-model="form.name"
                  :max-length="255"
                  floating-label="Наименование"
                  :clearable="true"
                  :required-mark="true"
                  :required="isSubmitted && nameError"
                  :required-condition="isSubmitted && nameError"
                  :class="{ 'is-invalid': isSubmitted && nameError }"
                />
              </div>

              <div v-if="!violation || (violation && !violation.is_system)" class="row">
                <div class="col col-12 col-md-5 col-xl-3">
                  <c-input
                    v-model="form.module"
                    type="select"
                    :items="collections.modules"
                    :clearable="true"
                    floating-label="Основная единица измерения"
                  />
                </div>
                <div class="col col-12 col-md-5 col-xl-3">
                  <c-switcher
                    v-model="form.can_change_in_post_settings"
                    class="can-change-in-post-settings"
                    type="checkbox"
                    label="Можно менять в настройках должности"
                  />
                </div>
              </div>

              <div v-if="!violation || (violation && !violation.is_system)" class="row" style="margin-bottom: 10px">
                <div class="col col-12 col-md-10 col-xl-6">
                  <c-switcher
                    v-model="form.quantitative_indicator"
                    class="quantitative-indicator"
                    type="checkbox"
                    label="Количественный показатель для норматива"
                    label-full-name="Количественный показатель для норматива"
                  />
                </div>
              </div>
              
              <div v-if="violation && violation.is_system" class="system">
                <div class="system__item">
                  <div class="system__item-name">Наименование:</div>
                  <div class="system__item-value">{{ violation.name}}</div>
                </div>
                <div class="system__item">
                  <div class="system__item-name">Основная единица измерения:</div>
                  <div class="system__item-value">{{ form.can_change_in_post_settings ? form.module.label + ', можно менять в настройках должности' : form.module.label }}</div>
                </div>
                <div class="system__item">
                  <div class="system__item-name">Количественный показатель:</div>
                  <div class="system__item-value">{{ violation.quantitative_indicator ? 'Да' : 'Нет' }}</div>
                </div>
              </div>

              <div class="form-group violation-type__description">
                <c-textarea
                  v-model="form.description"
                  class="description"
                  floating-label="Описание"
                  :clearable="true"
                />
              </div>
            </div>

            <div class="violation-type__top">
              <top-button class="top-button" :root="`#violation-type-scroll-${_uid}`" />
            </div>

            <div class="violation-type__actions">
              <c-button size="large" class="violation-type__reset" label="Отменить" @click="close" />
              <c-button class="violation-type__save" label="Сохранить" color="primary" size="large" @click="submit" />
            </div>
          </div>
        </shadow-scroll-container>
      </div>
      <div class="modal-actions">
        <c-button size="large" label="Отменить" @click="close" />
        <c-button label="Сохранить" color="primary" size="large" @click="submit" />
      </div>
    </div>
  </c-sidesheet>
</template>

<script>
import { mapState } from "vuex";
import CInput from "@/components/controls2/Input";
import CTextarea from "@/components/controls/textarea";
import CButton from "@/components/controls2/Button";
import CCheckbox from "@/components/checkbox";
import CSidesheet from "@/components/sidesheet";
import MConfirm from "@/components/modals/confirm-modal";
import FileLoader from "@/components/file-loader";
import TopButton from "@/components/top-button";
import ShadowScrollContainer from "@/components/shadow-scroll-container";
import Switcher from "@/components/controls/switcher";

import { MoneySourceType, formatMoneySourceType } from "@/entities/models/money-source-type";

import { isURL } from "@/utils/validate/url";
import { filterArrayByArray } from "@/utils/array/filter-array";

export default {
  name: "violation-types-create-modal",
  components: {
    "c-switcher": Switcher,
    CInput,
    CTextarea,
    CButton,
    CCheckbox,
    CSidesheet,
    MConfirm,
    FileLoader,
    TopButton,
    ShadowScrollContainer,
  },

  async created() {
    await this.$store.dispatch("positions/getUnits").then(res => {
      this.collections.modules = res.map(item => ({
        value: item.id,
        label: item.name
      }))
    })
  },

  data() {
    return {
      isSubmitted: false,
      displayModal: false,
      violation: null,

      form: {
        name: "",
        module: "",
        can_change_in_post_settings: false,
        quantitative_indicator: false,

        description: "",
        link: "",
        group: null,
        positions: [],
        points: "",
        important: false,
        files: [],

        fileLinks: [],
      },

      collections: {
        modules: [],
      }
    };
  },
  computed: {
    ...mapState({
      violationGroups: (state) => state.referenceLoadTypes.violationGroups,
      positions: (state) => state.referenceLoadTypes.positions,
    }),
    modalTitle() {
      return this.violation ? "Редактирование типа нагрузки" : "Новый тип нагрузки";
    },
    violationGroupsList() {
      return this.violationGroups.map((g) => ({ label: g.name, value: g.id }));
    },

    nameError() {
      return this.form.name.trim() ? null : "Обязательное поле";
    },
    pointsError() {
      if (!this.form.points) {
        return null;
      }

      const points = Number(this.form.points);

      if (points >= 0 && points <= 100) {
        return null;
      }

      return "Диапазон от 0 до 100";
    },
    linkError() {
      const link = this.form.link.trim();
      if (!link) return null;
      return isURL(link) ? null : "Неверный формат";
    },

    isValid() {
      return !this.nameError && !this.pointsError && !this.linkError;
    },
  },
  methods: {
    onScroll(scrollTop) {
      this.scrolled = scrollTop > 150;
    },

    open(data, params = {}) {
      if (data) {
        this.setData(data);
      }

      this.displayModal = true;

      // if (params.focusOnDescription) {
      //   this.$nextTick(() => {
      //     const field = this.$el.querySelector(".description");
      //     if (field) field.querySelector("textarea").focus();
      //   });
      // }
    },

    setData(data) {
      this.violation = data;

      const { name, group, description, module: itemModule } = this.violation;

      this.form.description = description;
      this.form.name = name;
      this.form.group = group ? this.violationGroupsList.find((g) => g.value.toString() === group.toString()) : null;

      console.log(this.violation)

      if (itemModule === "฿") this.form.module = this.collections.modules.find(moduleItem => moduleItem.label === "Рубли")
      if (itemModule === "шт") this.form.module = this.collections.modules.find(moduleItem => moduleItem.label === "Штуки")
      if (itemModule === "мин") this.form.module = this.collections.modules.find(moduleItem => moduleItem.label === "Норма времени")

      this.form.can_change_in_post_settings = Boolean(data.can_change_in_post_settings);
      this.form.quantitative_indicator = Boolean(data.quantitative_indicator);
    },

    close() {
      this.displayModal = false;
      this.reset();
    },

    reset() {
      this.isSubmitted = false;

      this.violation = null;
      this.form.name = "";
      this.form.group = null;
      this.form.description = "";

      this.form.module = "";
      this.form.can_change_in_post_settings = false;
      this.form.quantitative_indicator = false;

      // this.$refs.loader.clear();
    },

    getParams() {
      let itemModule = "";
      let unitId = 0;
      
      if (this.form.module) {
        if (this.form.module.label === "Штуки") {
          itemModule = "шт";
          unitId = 1;
        }
        if (this.form.module.label === "Рубли") {
          itemModule = "฿";
          unitId = 2;
        }
        if (this.form.module.label === "Норма времени") {
          itemModule = "мин";
          unitId = 3;
        }
      }

      const params = {
        "TypeLoad[name]": this.form.name,
        "TypeLoad[module]": itemModule || null,
        "TypeLoad[can_change_in_post_settings]": this.form.can_change_in_post_settings ? 1 : 0,
        "TypeLoad[quantitative_indicator]": this.form.quantitative_indicator ? 1 : 0,
        "TypeLoad[description]": this.form.description || null,
        "TypeLoad[unit_id]": unitId || null,
      }

      if (this.violation) params.code = this.violation.code

      return params;
    },

    submit() {
      this.isSubmitted = true;

      if (!this.isValid) {
        return;
      }

      const params = this.getParams();
      const action = this.violation
        ? "referenceLoadTypes/updateViolation"
        : "referenceLoadTypes/createViolation";

      this.$store.dispatch(action, params).then(res => {
        this.$emit('submitted')
      })

      this.close();
    },

    remove() {
      this.$refs.confirm.open({
        title: "Удаление типа нагрузки",
        text: `Тип нагрузки <b>${this.violation.name}</b> будет удален без возможности восстановления`,
        confirm: "Удалить",
        onConfirm: () => {
          this.removeRequest().then(() => {
            this.$emit('submitted')
            this.close();
          });
        },
      });
    },

    removeRequest() {
      return this.$store.dispatch("referenceLoadTypes/deleteListGroupItem", {
        code: this.violation.code
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
@import "~@/assets/styles/grid.scss";

::v-deep {
  .c-input-container_left {
    @include vp-767 {
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  .sidesheet {
    &__header {
      display: none;
    }
    &__wrapper {
      padding: 0 !important;
      z-index: 99999999999999;
    }

    &__content {
      height: 100%;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    &__close {
      padding-right: 10px;
      padding-top: 10px;
    }
  }
}

.modal-frame {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .modal-wrapper {
    flex-grow: 1;
    display: flex;
    overflow: hidden;
  }

  .modal-track {
    width: 100%;
    height: 100%;

    /deep/ {
      .scroll-container {
        padding-right: 0;
      }
    }
  }

  .modal-actions {
    flex-shrink: 0;
    background: white;
    display: flex;

    & button {
      flex-basis: 100%;
      width: 100%;
      border-radius: 0;
      flex-shrink: 1;
    }
  }

  &__header {
    margin-bottom: 30px;
  }

  &__title {
    margin-bottom: 12px;
    font-size: 18px;
    font-weight: 500;
  }

  &__actions {
    margin-bottom: 12px;
    display: flex;
    align-items: center;
  }

  @include bootstrapMd {
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    &__title {
      font-size: 22px;
      margin-bottom: 0;
    }
    &__actions {
      margin-bottom: 0;
    }
  }
}

.violation-type {
  &__wrapper {
    padding: 30px 20px 20px;
  }

  &__category {
    padding-top: 6px;
  }

  .is-invalid {
    padding-bottom: 20px;
  }

  &__actions {
    display: none;
    flex-shrink: 0;
  }
}

@include bootstrapMd {
  .violation-type {
    @include gridGenerator(11);

    padding-left: 30px;
    padding-right: 30px;
    padding-bottom: 40px;
    padding-top: 25px;

    &__importance {
      padding-top: 14px;
    }

    &__wrapper {
      padding: 0 0 16px;
    }

    &__actions {
      display: flex;
      & button {
        margin-right: 10px;
      }
    }
  }
  .modal-frame {
    .modal-actions {
      display: none;
    }
  }
}

.form-group {
  max-width: 564px;
}
.row {
  align-items: flex-end;
}
.quantitative-indicator {
  margin-top: 25px;
}

.system {
}
.system__item {
  &:not(:last-child) {
    margin-bottom: 10px;
  }
}
.system__item-name {
  color: #8E9CBB;
  line-height: 150%;
}
.system__item-value {
}

.can-change-in-post-settings {
  @include vp-767 {
    margin-top: 25px;
  }
}
</style>
